import axios from 'axios';
import moment from 'moment';
import { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import { FaRegCircleXmark, FaRegFloppyDisk, FaRegPenToSquare } from 'react-icons/fa6';
import { toast } from 'react-toastify';

import 'moment/locale/fr';
import styles from './Global.module.scss';
import { IExpense } from '../../../../../types';
import { IconWrapper } from '../../../../IconWrapper/IconWrapper';

interface IGlobalProps {
  expense: IExpense;
  onChange: () => void;
}

const Global = ({ expense, onChange }: IGlobalProps) => {
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    setName(expense.name);
    setStartDate(expense.start_date ? moment(expense.start_date).format('YYYY-MM-DD') : '');
    setEndDate(expense.end_date ? moment(expense.end_date).format('YYYY-MM-DD') : '');
  }, [expense]);

  const handleSave = () => {
    const updatedExpense = {
      name,
      ...(expense.type === 'trip' && {
        start_date: startDate,
        end_date: endDate,
      }),
    };

    axios.put(`/api/expense/${expense._id}`, updatedExpense).then(() => {
      setEditMode(false);
      onChange();
    }).catch((error) => {
      toast.error(error.response?.data ?? 'Unable to update expense report');
    });
  };

  const handleCancel = () => {
    setEditMode(false);
    setName(expense.name);
    setStartDate(expense.start_date ? moment(expense.start_date).format('YYYY-MM-DD') : '');
    setEndDate(expense.end_date ? moment(expense.end_date).format('YYYY-MM-DD') : '');
  };

  return (
    <Card className={styles.Card}>
    <Card.Header className="d-flex justify-content-between">
      {editMode ? (
        <Form.Control value={name} onChange={(event) => setName(event.target.value)} />
      ) : (
        <span>{expense.name}</span>
      )}
      {!editMode && (
        <Button size="sm" variant="warning" title="Edit" onClick={() => setEditMode(true)}>
          <IconWrapper icon={FaRegPenToSquare} />
        </Button>
      )}
      {editMode && (
        <ButtonGroup aria-label="Actions">
          <Button size="sm" variant="primary" onClick={handleSave} title="Save">
            <IconWrapper icon={FaRegFloppyDisk} />
          </Button>
          <Button size="sm" variant="secondary" onClick={handleCancel} title="Cancel">
            <IconWrapper icon={FaRegCircleXmark} />
          </Button>
        </ButtonGroup>
      )}
    </Card.Header>
    <Card.Body>
      <Row className="mb-2">
        <Col>
          <Form.Label htmlFor="date">
            {expense.type === 'trip' ? 'Period' : 'Date'}
          </Form.Label>
          <Form.Control
            name="date"
            type={editMode && expense.type === 'trip' ? 'date' : 'text'}
            value={
              editMode && expense.type === 'trip'
                ? startDate
                : expense.type === 'trip' && expense.start_date && expense.end_date
                  ? `${moment(expense.start_date).format('LL')} - ${moment(expense.end_date).format('LL')}`
                  : moment(expense.date).format('LL')
            }
            onChange={(event) => setStartDate(event.target.value)}
            readOnly={!editMode || expense.type !== 'trip'}
          />
          {editMode && expense.type === 'trip' && (
            <>
              <Form.Label className="mt-2" htmlFor="end_date">End date</Form.Label>
              <Form.Control
                name="end_date"
                type="date"
                value={endDate}
                min={startDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </>
          )}
        </Col>
      </Row>
      <Row className="mb-2">
        <Col>
          <Form.Label htmlFor="type">Type</Form.Label>
          <Form.Control name="type" value={expense.type} readOnly />
        </Col>
      </Row>
    </Card.Body>
  </Card>
  );
};

export default Global;
